// One stock item (spec/13 §6.4 Item history; `inventory:read`): what it has on hand now — the figure first, its state
// in words, its par, average cost and value — and two tabs: **History**, the movement ledger newest first (sold,
// refunded, received, wasted with its reason, staff meal, issued, counted, adjusted), each with who, when, the change,
// what was left after it, its value (with `inventory:reports`) and a link to the sale, delivery or count it came from,
// filtered by kind and by how far back; and **Details**, its packs, codes and note.
//
// The actions — Receive, Record usage, Count, Correct, Edit — each open their own dialog in this one's place (one
// dialog at a time), already on this item.

import type { StockItem, StockMovement, StockMovementKind } from '@clavian/api-client';
import { REPORT_PRESETS, reportRangeOf, type ReportPreset, type ReportRange } from '@clavian/domain/reports';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Dialog, Select, Table, Tabs, type TableColumn } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Facts, TableSkeleton } from '../../ui';
import type { StockAccess } from './access';
import { NO_FIGURE, useStockFormat } from './format';
import { activePacks, LevelBadge } from './parts';
import { useStockItem, useStockMovements, type MovementFilter } from './queries';
import { useWhen, type StockDialog } from './StockPage';

const MOVEMENT_KINDS: readonly StockMovementKind[] = ['sale', 'refund', 'receive', 'waste', 'staff_meal', 'issue', 'count', 'adjust'];
/** How far back the history reads: everything, or one of the reports' ranges of the venue's business days. */
type Span = 'all' | ReportPreset;
const SPANS: readonly Span[] = ['all', ...REPORT_PRESETS];

export function ItemDetailDialog({
  stockItemId,
  access,
  onClose,
  onAction,
}: {
  readonly stockItemId: string | null;
  readonly access: StockAccess;
  readonly onClose: () => void;
  readonly onAction: (dialog: StockDialog) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const item = useStockItem(venue.organizationId, venue.id, stockItemId);
  const data = item.data;
  return (
    <Dialog
      open={stockItemId !== null}
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="xl"
      eyebrow={data === undefined ? t('stock.detail.eyebrow') : t(`stock.kind.${data.kind}`)}
      title={data?.name ?? t('stock.detail.loading')}
      description={data === undefined ? undefined : t('stock.detail.description')}
    >
      {data === undefined ? (
        item.isError ? (
          <LoadFailure error={item.error} message={t('stock.detail.unavailable')} onRetry={() => void item.refetch()} />
        ) : (
          <TableSkeleton rows={5} label={t('stock.loading')} />
        )
      ) : (
        <ItemDetail item={data} access={access} onAction={onAction} />
      )}
    </Dialog>
  );
}

function ItemDetail({ item, access, onAction }: { readonly item: StockItem; readonly access: StockAccess; readonly onAction: (dialog: StockDialog) => void }): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  const when = useWhen();
  const [tab, setTab] = useState<'history' | 'details'>('history');
  const supply = item.kind === 'supply';
  const allowed = (any: boolean, onlySupplies: boolean) => any && (!onlySupplies || supply) && !item.archived;

  return (
    <div className="console-stock-detail">
      <div className="console-stock-detail__hero">
        <div className="console-stock-detail__figure">
          <span className="console-stock-detail__label">{t('stock.detail.onHand')}</span>
          <span className={item.onHand < 0 ? 'console-stock-detail__onhand console-stock-row__negative' : 'console-stock-detail__onhand'}>{stock.quantity(item.onHand, item)}</span>
          <LevelBadge level={item.level} />
        </div>
        <Facts
          columns={3}
          className="console-stock-detail__facts"
          items={[
            { key: 'par', label: t('stock.column.par'), value: <span className="console-num">{item.parLevel === null ? t('stock.detail.noPar') : stock.quantity(item.parLevel, item)}</span> },
            ...(access.reports
              ? [
                  { key: 'cost', label: t('stock.column.averageCost'), value: <span className="console-num">{item.averageUnitCost === null ? t('stock.detail.notCosted') : stock.unitCost(item.averageUnitCost, item)}</span> },
                  { key: 'value', label: t('stock.column.value'), value: <span className="console-money">{stock.money(item.value)}</span> },
                ]
              : []),
            { key: 'counted', label: t('stock.detail.lastCounted'), value: <span className="console-num">{item.lastCountedAt === null ? t('stock.detail.neverCounted') : when(item.lastCountedAt)}</span> },
          ]}
        />
      </div>
      {item.level === 'negative' ? (
        <p className="console-stock-detail__advice" role="note">
          <Icon name="alert" /> {t('stock.detail.negativeAdvice')}
        </p>
      ) : null}
      <div className="console-stock-detail__actions" role="group" aria-label={t('stock.detail.actions')}>
        {allowed(access.receive, access.receiveSuppliesOnly) ? (
          <Button size="sm" variant="secondary" icon={<Icon name="download" />} onClick={() => onAction({ kind: 'receive', item })}>
            {t('stock.actions.receive')}
          </Button>
        ) : null}
        {allowed(access.usage, access.usageSuppliesOnly) ? (
          <Button size="sm" variant="secondary" icon={<Icon name="minus" />} onClick={() => onAction({ kind: 'usage', item, mode: supply ? 'issue' : 'waste' })}>
            {t('stock.actions.usage')}
          </Button>
        ) : null}
        {allowed(access.count, access.countSuppliesOnly) ? (
          <Button size="sm" variant="secondary" icon={<Icon name="list" />} onClick={() => onAction({ kind: 'count', item })}>
            {t('stock.items.countThis')}
          </Button>
        ) : null}
        {allowed(access.adjust, access.adjustSuppliesOnly) ? (
          <Button size="sm" variant="secondary" icon={<Icon name="pencil" />} onClick={() => onAction({ kind: 'usage', item, mode: 'adjust' })}>
            {t('stock.items.adjust')}
          </Button>
        ) : null}
        {access.items && (!access.manageSuppliesOnly || supply) ? (
          <Button size="sm" variant="ghost" onClick={() => onAction({ kind: 'editItem', item })}>
            {t('stock.items.edit')}
          </Button>
        ) : null}
      </div>
      <Tabs
        label={t('stock.detail.tabs')}
        value={tab}
        onValueChange={(value) => setTab(value as 'history' | 'details')}
        items={[
          { value: 'history', label: t('stock.detail.history'), content: tab === 'history' ? <History item={item} reports={access.reports} /> : null },
          { value: 'details', label: t('stock.detail.details'), content: tab === 'details' ? <Details item={item} /> : null },
        ]}
      />
    </div>
  );
}

/** The movement ledger for the item, newest first, a page at a time. */
function History({ item, reports }: { readonly item: StockItem; readonly reports: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const stock = useStockFormat();
  const when = useWhen();
  const [kind, setKind] = useState<StockMovementKind | 'all'>('all');
  const format = useVenueFormatter();
  // The venue's business days the span covers, worked out when it is chosen, so the query key stays put while the
  // dialog is open; the server reads them in the venue's timezone and cutoff.
  const [{ span, days }, setSpanDays] = useState<{ readonly span: Span; readonly days?: ReportRange }>({ span: 'all' });
  const setSpan = (next: Span) => setSpanDays(next === 'all' ? { span: next } : { span: next, days: reportRangeOf(next, format.businessDate(new Date().toISOString())) });
  const filter: MovementFilter = { stockItemId: item.id, ...(kind === 'all' ? {} : { kind }), ...(days === undefined ? {} : { fromDay: days.from, toDay: days.to }) };
  const movements = useStockMovements(venue.organizationId, venue.id, filter);
  const rows = (movements.data?.pages ?? []).flatMap((page) => page.data);

  const columns: TableColumn<StockMovement>[] = [
    { key: 'when', header: t('stock.history.when'), cell: (movement) => <span className="console-num">{when(movement.occurredAt)}</span> },
    { key: 'what', header: t('stock.history.what'), cell: (movement) => <MovementWhat movement={movement} /> },
    {
      key: 'change',
      header: t('stock.history.change'),
      numeric: true,
      cell: (movement) => <span className={movement.quantity < 0 ? 'console-num console-stock-change--out' : 'console-num console-stock-change--in'}>{stock.change(movement.quantity, item)}</span>,
    },
    { key: 'after', header: t('stock.history.after'), numeric: true, cell: (movement) => <span className="console-num console-muted">{stock.quantity(movement.quantityAfter, item)}</span> },
    ...(reports ? [{ key: 'value', header: t('stock.history.value'), numeric: true, cell: (movement: StockMovement) => <span className="console-money console-muted">{signedMoney(stock.money(movement.value), movement.value?.amount ?? 0)}</span> }] : []),
    { key: 'who', header: t('stock.history.who'), cell: (movement) => <span className="console-muted">{movement.recordedByName ?? t('stock.history.system')}</span> },
  ];

  return (
    <div className="console-stock-history">
      <div className="console-stock-history__filters" role="group" aria-label={t('stock.history.filters')}>
        <Select
          aria-label={t('stock.history.kindFilter')}
          value={kind}
          onValueChange={(value) => setKind(value as StockMovementKind | 'all')}
          options={[{ value: 'all', label: t('stock.history.allKinds') }, ...MOVEMENT_KINDS.map((one) => ({ value: one, label: t(`stock.movement.${one}`) }))]}
        />
        <Select aria-label={t('stock.history.spanFilter')} value={span} onValueChange={(value) => setSpan(value as Span)} options={SPANS.map((one) => ({ value: one, label: one === 'all' ? t('stock.history.allTime') : t(`reports.range.${one}`) }))} />
      </div>
      {movements.data === undefined ? (
        movements.isError ? (
          <LoadFailure error={movements.error} message={t('stock.history.unavailable')} onRetry={() => void movements.refetch()} />
        ) : (
          <TableSkeleton rows={6} label={t('stock.loading')} />
        )
      ) : (
        <>
          <div className="console-table-card">
            <Table caption={t('stock.history.caption', { name: item.name })} captionHidden columns={columns} rows={rows} rowKey={(movement) => movement.id} empty={t('stock.history.empty')} />
          </div>
          {movements.hasNextPage ? (
            <div className="console-list-foot">
              <Button variant="secondary" size="sm" busy={movements.isFetchingNextPage} onClick={() => void movements.fetchNextPage()}>
                {t('stock.history.more')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/** A value with its sign spelled out: "−₹21.60". */
function signedMoney(text: string, amount: number): string {
  return text !== NO_FIGURE && amount > 0 ? `+${text}` : text;
}

/** What a movement was, in words, with its reason or note and a link to where it came from. */
function MovementWhat({ movement }: { readonly movement: StockMovement }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const detail = [movement.reason === null ? null : t(`stock.wasteReason.${movement.reason}`), movement.note].filter((part): part is string => part !== null && part !== '');
  let link: ReactNode = null;
  if (movement.saleId !== null) {
    link = (
      <Link to="/v/$venueSlug/orders" params={{ venueSlug: venue.slug }} search={{ sale: movement.saleId }} className="console-stock-link">
        {t('stock.history.openSale')}
      </Link>
    );
  } else if (movement.receiptId !== null) {
    link = (
      <Link to="/v/$venueSlug/stock/$tab" params={{ venueSlug: venue.slug, tab: 'deliveries' }} search={{ receipt: movement.receiptId }} className="console-stock-link">
        {t('stock.history.openDelivery')}
      </Link>
    );
  } else if (movement.countId !== null) {
    link = (
      <Link to="/v/$venueSlug/stock/counts/$countId" params={{ venueSlug: venue.slug, countId: movement.countId }} className="console-stock-link">
        {t('stock.history.openCount')}
      </Link>
    );
  }
  return (
    <span className="console-stock-what">
      <span className="console-stock-what__kind">{t(`stock.movement.${movement.kind}`)}</span>
      {detail.length > 0 ? <span className="console-cell-sub">{detail.join(' · ')}</span> : null}
      {link}
    </span>
  );
}

/** The item's packs, codes and note. */
function Details({ item }: { readonly item: StockItem }): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  const packs = activePacks(item);
  return (
    <div className="console-stock-details">
      <Facts
        columns={3}
        items={[
          { key: 'unit', label: t('stock.editor.baseUnit'), value: t(`stock.baseUnit.${item.baseUnit}`) },
          { key: 'sku', label: t('stock.editor.sku'), value: <span translate="no">{item.sku ?? NO_FIGURE}</span> },
          { key: 'barcode', label: t('stock.editor.barcode'), value: <span className="console-code" translate="no">{item.barcode ?? NO_FIGURE}</span> },
          ...(item.productId === null ? [] : [{ key: 'product', label: t('stock.detail.product'), value: t('stock.detail.productLinked') }]),
          ...(item.note === null ? [] : [{ key: 'note', label: t('stock.editor.note'), value: item.note }]),
        ]}
      />
      <h3 className="console-stock-details__title">{t('stock.editor.packs')}</h3>
      {packs.length === 0 ? (
        <p className="console-subtle">{t('stock.detail.noPacks')}</p>
      ) : (
        <ul className="console-stock-details__packs">
          {packs.map((pack) => (
            <li key={pack.id}>
              <span className="console-stock-details__pack-name">{pack.name}</span>
              <span className="console-num">{t('stock.detail.packHolds', { size: stock.quantity(pack.quantity, item) })}</span>
              {pack.barcode === null ? null : <span className="console-code console-subtle" translate="no">{pack.barcode}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
